import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, FileText, LogOut, Lock, Calendar, CreditCard, Clock, CheckCircle,
  FileSearch, ClipboardCheck, Building2, Receipt, User, Briefcase
} from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../lib/BrandingContext';
import ModuleWalletDropdown from '../components/ModuleWalletDropdown';
import BuyCreditsModal from '../components/BuyCreditsModal';
import { WorkflowReport } from '../components/WorkflowTimer';

const TenantUserDashboard = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [featureToggles, setFeatureToggles] = useState({
    create_legal_opinion: true,
    ec_extractor: true,
    institution_checklist: true,
    missing_doc_identifier: true,
    cs_dashboard: true,
    legal_opinion_validator: true,
    online_builder_opinion: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats();
    fetchFeatureToggles();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Handle both formats - direct toggles or {toggles: {...}} wrapper
      const toggles = response.data.toggles || response.data;
      setFeatureToggles(toggles);
    } catch (err) {
      console.error('Error fetching feature toggles:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.company_name || 'Logo'} className="h-10" />
              ) : (
                <div className="flex items-center space-x-2">
                  <Scale className="w-8 h-8 text-blue-600" />
                  <span className="text-xl font-bold text-gray-900">
                    {branding?.company_name || 'TechOrange-Legal'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                {user?.role && (
                  <p className="text-xs text-blue-600 font-medium capitalize">{user.role.replace('_', ' ')}</p>
                )}
              </div>
              
              <ModuleWalletDropdown />
              
              <button
                onClick={() => navigate('/password-management')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Password Management"
              >
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-4 space-y-2">
            {/* My Account Section - Always at top */}
            <div className="pb-4 mb-4 border-b border-gray-200">
              <div className="px-4 py-2 text-gray-700 font-semibold flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>My Account</span>
              </div>
              <div className="ml-4 space-y-1">
                <button
                  onClick={() => navigate('/tenant-profile')}
                  className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded flex items-center space-x-2 font-medium"
                  data-testid="nav-tenant-profile"
                >
                  <Building2 className="w-4 h-4" />
                  <span>Tenant Profile</span>
                </button>
                <button
                  onClick={() => navigate('/settings/advocate-profile')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center space-x-2 font-medium"
                  data-testid="nav-advocate-profile"
                >
                  <Briefcase className="w-4 h-4" />
                  <span>Advocate Profile</span>
                </button>
                <button
                  onClick={() => navigate('/settings/billing-profile')}
                  className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 rounded flex items-center space-x-2 font-medium"
                  data-testid="nav-billing-profile"
                >
                  <Receipt className="w-4 h-4" />
                  <span>Billing & GST</span>
                </button>
                <button
                  onClick={() => navigate('/invoices')}
                  className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded flex items-center space-x-2 font-medium"
                  data-testid="nav-invoices"
                >
                  <FileText className="w-4 h-4" />
                  <span>My Invoices</span>
                </button>
              </div>
            </div>

            {/* Home/Analytics Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold">Home / Analytics</div>
              <div className="ml-4 space-y-1">
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Legal Opinions Generated
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  EC Extractions Generated
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Bank Format Distribution
                </div>
              </div>
            </div>

            {/* Applications Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold">Applications</div>
              <div className="ml-4 space-y-1">
                {featureToggles.create_legal_opinion && (
                  <button
                    onClick={() => navigate('/extract')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Scale className="w-4 h-4 text-blue-500" />
                    <span>Create Legal Opinion</span>
                  </button>
                )}
                {featureToggles.online_builder_opinion && (
                  <button
                    onClick={() => navigate('/online-builder-opinion')}
                    className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50 rounded flex items-center space-x-2 font-medium"
                    data-testid="online-builder-opinion-nav"
                  >
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <span>Online Builder Opinion</span>
                  </button>
                )}
                {featureToggles.ec_extractor && (
                  <button
                    onClick={() => navigate('/ec-extractor')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <FileSearch className="w-4 h-4 text-green-500" />
                    <span>EC Extractor</span>
                  </button>
                )}
                {featureToggles.institution_checklist && (
                  <button
                    onClick={() => navigate('/institution-checklist')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <ClipboardCheck className="w-4 h-4 text-amber-500" />
                    <span>Institutional Checklist</span>
                  </button>
                )}
                {featureToggles.missing_doc_identifier && (
                  <button
                    onClick={() => navigate('/missing-docs')}
                    className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded font-medium"
                  >
                    📋 Missing Doc Identifier
                  </button>
                )}
                {featureToggles.cs_dashboard && (
                  <div className="space-y-0">
                    <div className="px-4 py-2 text-sm text-indigo-600 font-medium flex items-center">
                      <span className="mr-2">🏢</span> CS Dashboard
                    </div>
                    <div className="ml-4 border-l-2 border-indigo-200 pl-2">
                      <button
                        onClick={() => navigate('/cs-report')}
                        className="w-full text-left px-3 py-1.5 text-sm text-indigo-500 hover:bg-indigo-50 rounded flex items-center"
                        data-testid="cs-report-nav"
                      >
                        <span className="mr-2">📋</span> CS Compliance Opinion Generator
                      </button>
                    </div>
                  </div>
                )}
                {featureToggles.legal_opinion_validator && (
                  <button
                    onClick={() => navigate('/legal-opinion-validation')}
                    className="w-full text-left px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded font-medium"
                    data-testid="love-dashboard-nav"
                  >
                    ⚖️ Legal Opinion Validator
                  </button>
                )}
              </div>
            </div>

            {/* Actions Section */}
            <div>
              <div className="px-4 py-2 text-gray-700 font-semibold">Actions</div>
              <div className="ml-4 space-y-1">
                <button
                  onClick={() => navigate('/chats')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                >
                  💬 Messages
                </button>
                <button
                  onClick={() => navigate('/my-opinions')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  View My Opinions
                </button>
                {/* Review Opinions - for approvers/reviewers/admins */}
                {(user?.role === 'approver' || user?.role === 'reviewer' || user?.role === 'tenant_admin') && (
                  <button
                    onClick={() => navigate('/review')}
                    className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded font-medium"
                  >
                    ✅ Review Opinions
                  </button>
                )}
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">My Dashboard</h2>

            {/* Stats Grid - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current Month Stats */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Current Month</h3>
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">My Legal Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_current_month_opinions || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">My EC Extractions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_current_month_ec || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">My APF Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_current_month_apf || 0}</p>
                  </div>
                </div>
              </div>

              {/* Monthly Average */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Monthly</h3>
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Avg Opinions/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_avg_opinions_per_month || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg EC/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_avg_ec_per_month || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avg APF/Month</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_avg_apf_per_month || 0}</p>
                  </div>
                </div>
              </div>

              {/* Total Till Date */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Till Date</h3>
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">My Total Opinions</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_total_opinions || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">My Total EC</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_total_ec || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">My Total APF</p>
                    <p className="text-2xl font-bold text-gray-900">{stats?.my_total_apf || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {featureToggles.create_legal_opinion && (
                  <button
                    onClick={() => navigate('/extract')}
                    className="bg-white hover:bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Scale className="w-6 h-6 text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Create Legal Opinion</h4>
                    </div>
                    <p className="text-sm text-gray-600">Generate a new retail legal opinion</p>
                  </button>
                )}

                {featureToggles.ec_extractor && (
                  <button
                    onClick={() => navigate('/ec-extractor')}
                    className="bg-white hover:bg-green-50 border-2 border-green-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <FileSearch className="w-6 h-6 text-green-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">EC Extractor</h4>
                    </div>
                    <p className="text-sm text-gray-600">Extract EC data</p>
                  </button>
                )}

                {featureToggles.institution_checklist && (
                  <button
                    onClick={() => navigate('/institution-checklist')}
                    className="bg-white hover:bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-amber-100 p-2 rounded-lg">
                        <ClipboardCheck className="w-6 h-6 text-amber-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Institutional Checklist</h4>
                    </div>
                    <p className="text-sm text-gray-600">Generate institutional legal opinions</p>
                  </button>
                )}

                <button
                  onClick={() => navigate('/my-opinions')}
                  className="bg-white hover:bg-purple-50 border-2 border-purple-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">My Opinions</h4>
                  </div>
                  <p className="text-sm text-gray-600">View all my generated opinions</p>
                </button>

                {/* Review Opinions - for approvers/reviewers/admins */}
                {(user?.role === 'approver' || user?.role === 'reviewer' || user?.role === 'tenant_admin') && (
                  <button
                    onClick={() => navigate('/review')}
                    className="bg-white hover:bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-orange-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Review Opinions</h4>
                    </div>
                    <p className="text-sm text-gray-600">Review and approve pending opinions</p>
                  </button>
                )}
              </div>
            </div>

            {/* Workflow Timing Report */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  📊 Workflow Timing Report
                </h3>
                <span className="text-sm text-gray-500">Processing times</span>
              </div>
              <WorkflowReport days={30} limit={15} tenantId={user?.tenant_id} />
            </div>
          </div>
        </main>
      </div>

      {/* Buy Credits Modal */}
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
};

export default TenantUserDashboard;
